import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import parse from 'html-react-parser';
import React from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
    fontSize: 8,
    color: '#1e293b'
  },
  heading: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 10,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1
  },
  line: {
    height: 0.5,
    backgroundColor: '#cbd5e1',
    marginVertical: 8
  },
  line2: {
    height: 0.5,
    backgroundColor: '#cbd5e1',
    marginVertical: 8
  },
  boldText: {
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: 8,
    color: '#0f172a'
  },
  column: {
    flex: 1,
    paddingHorizontal: 4
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4
  },
  text: {
    fontSize: 8,
    color: '#1e293b',
    lineHeight: 1.2
  },
  textBold: {
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#334155'
  },
  textUnderlined: {
    textDecoration: 'underline'
  },

  // ─── Document structure ───────────────────────────────────────────────────
  section: {
    marginBottom: 8,
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    backgroundColor: '#ffffff'
  },
  sectionHeader: {
    fontSize: 8.5,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
    padding: '4px 8px',
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  contentContainer: {
    flexDirection: 'row',
    padding: 6,
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    backgroundColor: '#f8fafc',
    marginBottom: 8
  },
  contentContainer2: {
    flexDirection: 'row',
    padding: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0'
  },

  // ─── Alignment layouts ────────────────────────────────────────────────────
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    width: 95,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    fontSize: 8,
    flexShrink: 0
  },
  fieldValue: {
    flex: 1,
    fontSize: 8,
    color: '#0f172a',
    flexWrap: 'wrap'
  },
  fullWidthRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#f1f5f9',
    padding: '5px 8px',
    alignItems: 'flex-start'
  },
  fullWidthLabel: {
    width: 120,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    fontSize: 8,
    flexShrink: 0
  },
  fullWidthValue: {
    flex: 1,
    fontSize: 8,
    color: '#0f172a',
    flexWrap: 'wrap'
  },
  clinicalContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 4
  },
  clinicalItem: {
    width: '50%',
    flexDirection: 'row',
    padding: '3px 6px',
    alignItems: 'flex-start'
  },
  clinicalLabel: {
    width: 125,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    fontSize: 8,
    flexShrink: 0
  },
  clinicalValue: {
    flex: 1,
    fontSize: 8,
    color: '#0f172a',
    flexWrap: 'wrap'
  },

  // ─── Medicine Table ───────────────────────────────────────────────────────
  // FIX: All column widths must be fixed px. Total = 30+150+50+100+70+50 = 450px
  // Page inner width ~535px so this fits comfortably inside Section border.
  tableContainer: {
    width: '100%',
    marginVertical: 5,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0f172a',
    paddingVertical: 5,
    paddingHorizontal: 4,   // FIX: was 2, slight breathing room
    alignItems: 'center',
  },
  // FIX: Renamed from tableRow → medicineRow to avoid collision with HTML parser tableRow
  medicineRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 6,
    paddingHorizontal: 4,   // FIX: was 2, match header padding
    alignItems: 'center',
    minHeight: 28,
  },
  medicineRowEven: {
    backgroundColor: '#f8fafc'
  },
  medicineRowOdd: {
    backgroundColor: '#ffffff'
  },
  // Fixed pixel widths for columns
  colSNo: {
    width: 30,
    textAlign: 'center',
    fontSize: 7,
  },
  colMed: {
    width: 150,
    textAlign: 'left',
    paddingLeft: 4,
    fontSize: 7,
    flexWrap: 'wrap',
  },
  colDose: {
    width: 50,
    textAlign: 'center',
    fontSize: 7,
  },
  colFreq: {
    width: 100,
    textAlign: 'left',
    paddingLeft: 4,
    fontSize: 7,
    flexWrap: 'wrap',
  },
  colSchedule: {
    width: 70,
    textAlign: 'center',
    fontSize: 7,
  },
  colDays: {
    width: 50,
    textAlign: 'center',
    fontSize: 7,
  },
  tableHeaderTitle: {
    color: '#ffffff',
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: 7
  },
  tableLegend: {
    flexDirection: 'row',
    padding: '4px 8px',
    backgroundColor: '#f1f5f9',
    borderTopWidth: 0.5,
    borderTopColor: '#cbd5e1',
    marginTop: 6,
    flexWrap: 'wrap'
  },
  tableLegendBold: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569'
  },
  tableLegendText: {
    fontSize: 7,
    color: '#64748b'
  },
  tableEmptyRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    minHeight: 28,
    alignItems: 'center'
  },
  tableEmptyText: {
    color: '#64748b',
    textAlign: 'center',
    fontSize: 7,
    width: '100%'
  },

  // ─── HTML parser table styles (renamed to avoid collision with medicine table) ─
  figure: {
    marginVertical: 4
  },
  htmlTable: {
    width: '100%',
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    marginTop: 4,
    marginBottom: 4
  },
  htmlTableBody: {
    flexDirection: 'column'
  },
  // FIX: was "tableRow" — renamed to "htmlTableRow" to avoid style collision
  htmlTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 4,
    paddingHorizontal: 2,
    alignItems: 'center',
  },
  tableCell: {
    flex: 1,
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    padding: 3
  },
  orderedList: {
    flexDirection: 'column',
    paddingLeft: 8
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 2
  },
  listItemText: {
    fontSize: 8,
    color: '#1e293b'
  },
  parsedContent: {
    padding: 8,
    fontSize: 8,
    lineHeight: 1.2
  },

  // ─── Signatures ───────────────────────────────────────────────────────────
  signatureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    paddingHorizontal: 8
  },
  signatureBlock: {
    alignItems: 'center',
    width: 160
  },
  signatureLine: {
    width: '100%',
    borderTopWidth: 0.5,
    borderTopColor: '#475569',
    marginTop: 15,
    marginBottom: 3
  }
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

// ─── Reusable PDF primitives ────────────────────────────────────────────────

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const Section = ({ title, children, style }) => (
  <View style={{ ...styles.section, ...style }}>
    <Text content={title} style={styles.sectionHeader} />
    {children}
  </View>
);

const InfoField = ({ label, value }) => (
  <View style={styles.fieldRow}>
    <Text content={label} style={styles.fieldLabel} />
    <Text content={value || '-'} style={styles.fieldValue} />
  </View>
);

const FullWidthField = ({ label, value, isLast }) => (
  <View style={{ ...styles.fullWidthRow, ...(isLast ? { borderBottomWidth: 0 } : {}) }}>
    <Text content={label} style={styles.fullWidthLabel} />
    <Text content={value || '-'} style={styles.fullWidthValue} />
  </View>
);

const ClinicalField = ({ label, value }) => (
  <View style={styles.clinicalItem}>
    <Text content={label} style={styles.clinicalLabel} />
    <Text content={value || '-'} style={styles.clinicalValue} />
  </View>
);

// ─── Main export ────────────────────────────────────────────────────────────

export const DischargeSummaryPDF = ({ data, isPublic = false }) => {
  const [barcodeImg, setBarcodeImg] = React.useState("");
  const [qrImg, setQrImg] = React.useState("");
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloaded, setIsDownloaded] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const formatRegTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };
    return date.toLocaleTimeString('en-US', options);
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  // ─── HTML → PDF rich text parser ─────────────────────────────────────────
  // FIX: all internal table views now use `htmlTableRow` instead of `tableRow`

  const parseHtmlToPdfText = (html) => {
    if (!html || html === '-') return <Text content="-" />;

    return parse(html, {
      replace: (domNode) => {
        if (domNode.type === 'tag') {
          switch (domNode.name) {
            case 'strong':
              return (
                <PDFText style={{ ...styles.boldText, fontFamily: 'Roboto', marginBottom: 3 }}>
                  {domNode.children.map((child) => child.data).join('')}
                </PDFText>
              );
            case 'p':
              return (
                <PDFText style={{ ...styles.text, marginBottom: 10 }}>
                  {domNode.children
                    .map((child) => {
                      if (child.type === 'text') return child.data.trim();
                      if (child.type === 'tag' && child.name === 'strong') {
                        return child.children.map((c) => c.data).join('');
                      }
                      return '';
                    })
                    .join(' ')}
                </PDFText>
              );
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return (
                <PDFText style={{ ...styles.boldText, fontFamily: 'Roboto', marginBottom: 5 }}>
                  {domNode.children.map((child) => child.data).join('')}
                </PDFText>
              );
            case 'figure':
              return (
                <PDFView style={styles.figure}>
                  {domNode.children.map((child, childIndex) => {
                    if (child.name === 'table') {
                      return (
                        // FIX: was styles.table → styles.htmlTable
                        <PDFView style={styles.htmlTable} key={childIndex}>
                          {child.children.map((tbody, tbodyIndex) => {
                            if (tbody.name === 'tbody') {
                              return (
                                // FIX: was styles.tableBody → styles.htmlTableBody
                                <PDFView style={styles.htmlTableBody} key={tbodyIndex}>
                                  {tbody.children.map((row, rowIndex) => {
                                    if (row.name === 'tr') {
                                      return (
                                        // FIX: was styles.tableRow → styles.htmlTableRow
                                        <PDFView style={styles.htmlTableRow} key={rowIndex}>
                                          {row.children.map((cell, cellIndex) => {
                                            if (cell.name === 'td' || cell.name === 'th') {
                                              return (
                                                <PDFView style={styles.tableCell} key={cellIndex}>
                                                  {cell.children.map((c, ci) => {
                                                    if (c.name === 'p') {
                                                      return (
                                                        <PDFText
                                                          style={{ ...styles.text, marginBottom: 4 }}
                                                          key={ci}
                                                        >
                                                          {c.children.map((pChild) => pChild.data).join('')}
                                                        </PDFText>
                                                      );
                                                    }
                                                    if (c.type === 'text') {
                                                      return (
                                                        <PDFText
                                                          style={{ ...styles.text, marginBottom: 4 }}
                                                          key={ci}
                                                        >
                                                          {c.data.trim()}
                                                        </PDFText>
                                                      );
                                                    }
                                                    return null;
                                                  })}
                                                </PDFView>
                                              );
                                            }
                                            return null;
                                          })}
                                        </PDFView>
                                      );
                                    }
                                    return null;
                                  })}
                                </PDFView>
                              );
                            }
                            return null;
                          })}
                        </PDFView>
                      );
                    }
                    return null;
                  })}
                </PDFView>
              );
            case 'ol':
              return (
                <PDFView style={styles.orderedList}>
                  {domNode.children.map((li, liIndex) => {
                    if (li.name === 'li') {
                      return (
                        <PDFView style={styles.listItem} key={liIndex}>
                          <PDFText style={{ ...styles.listItemText, marginBottom: 10 }}>
                            {`${liIndex + 1}. `}
                            {li.children.map((child) => child.data).join('')}
                          </PDFText>
                        </PDFView>
                      );
                    }
                    return null;
                  })}
                </PDFView>
              );
            case 'li':
              return (
                <PDFView style={styles.listItem}>
                  <PDFText style={{ ...styles.listItemText, marginBottom: 10 }}>
                    {domNode.children.map((child) => child.data).join('')}
                  </PDFText>
                </PDFView>
              );
            default:
              return (
                <PDFText style={{ ...styles.text, marginBottom: 10 }}>
                  {domNode.data}
                </PDFText>
              );
          }
        }
      }
    });
  };

  // ─── Effects ──────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!data || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const refId = params.get("refId");

    // Barcode
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(data.ipd_no), {
      format: "CODE128",
      displayValue: false,
      height: 40
    });
    setBarcodeImg(canvas.toDataURL());

    // QR → public link
    if (refId) {
      let path;
      if (isPublic) {
        path = window.location.pathname;
      } else {
        path = window.location.pathname.replace("/print/", "/public/print/");
      }
      const qrUrl = `${window.location.origin}${path}?refId=${refId}`;
      QRCode.toDataURL(qrUrl).then(setQrImg);
    }
  }, [data]);

  const downloadPDF = async () => {
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(PdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Discharge-Summary-${data?.receiptNo || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
        setIsDownloading(false);
        setIsDownloaded(true);
      }, 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
      setIsError(true);
    }
  };

  React.useEffect(() => {
    if (isPublic && data && barcodeImg && qrImg && !isDownloading) {
      const timer = setTimeout(() => {
        downloadPDF();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPublic, data, barcodeImg, qrImg, isDownloading]);

  // ─── Derived values ───────────────────────────────────────────────────────

  const followUpText = data?.next_follow_up
    ? (String(data.next_follow_up).toLowerCase().includes('day')
      ? data.next_follow_up
      : `${data.next_follow_up} Days`)
    : '-';

  const dischargeMode = data?.patient_details?.discharge_mode || '';
  // "Normal" discharge → standard title; all others get their mode name as prefix
  const reportTitle =
    !dischargeMode || dischargeMode.trim().toLowerCase() === 'normal'
      ? 'Discharge Summary'
      : `${dischargeMode} Summary`;

  // ─── PDF Document ─────────────────────────────────────────────────────────

  const PdfDoc = (
    <Document title={reportTitle}>
      <Page size="A4" style={styles.page}>

        {/* Hospital Header Logo */}
        <PDFImage
          src="/logo_Hospital_m.jpeg"
          style={{ width: '100%', height: 70, marginBottom: 5, objectFit: 'contain' }}
        />

        {/* Title — dynamic based on Discharge Mode */}
        <Text content={reportTitle} style={styles.heading} />

        {/* Barcode & QR Code Row */}
        <View style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: 'center',
          marginBottom: 8
        }}>
          {barcodeImg
            ? <PDFImage src={barcodeImg} style={{ width: 110, height: 30 }} />
            : <View />
          }
          {qrImg
            ? <PDFImage src={qrImg} style={{ width: 45, height: 45 }} />
            : <View />
          }
        </View>

        {/* ── Patient Demographics ─────────────────────────────────────── */}
        <View style={styles.contentContainer}>
          <View style={styles.column}>
            <InfoField label="Patient Name:" value={data?.name} />
            <InfoField label="Address:" value={data?.address} />
            <InfoField label="UHID No.:" value={data?.uhid} />
            <InfoField label="Mob No.:" value={data?.mobile} />
            <InfoField
              label="Admission Date:"
              value={`${formatDate(data?.date) || ''} ${formatRegTime(data?.time) || ''}`}
            />
            <InfoField label="Room No.:" value={data?.room_no} />
          </View>
          <View style={{ width: 10 }} />
          <View style={styles.column}>
            <InfoField label="Consultant:" value={data?.consultant} />
            <InfoField label="Age/Sex:" value={`${data?.age || ''} / ${data?.gender || ''}`} />
            <InfoField label="IPD No.:" value={data?.ipd_no} />
            <InfoField
              label="Discharge Date:"
              value={`${formatDate(data?.dis_date) || ''} ${formatRegTime(data?.patientDischarge?.time) || ''}`}
            />
            <InfoField label="Ward:" value={data?.ward_name} />
            <InfoField label="Discharge Mode:" value={data?.patient_details?.discharge_mode} />
            <InfoField label="Department:" value={data?.department} />
          </View>
        </View>

        {/* ── Diagnosis ────────────────────────────────────────────────── */}
        {data?.diagnos && data.diagnos.length > 0 && (
          <Section title="Diagnosis">
            {data.diagnos.map((item, index) => (
              <FullWidthField
                key={index}
                label={`Diagnosis ${index + 1}:`}
                value={item.add_diagnos}
                isLast={index === data.diagnos.length - 1}
              />
            ))}
          </Section>
        )}

        {/* ── Clinical Summary & Patient History ───────────────────────── */}
        {(data?.other_details?.final_diagnosis ||
          data?.other_details?.chief_complaints ||
          data?.other_details?.past_history ||
          data?.other_details?.history ||
          data?.other_details?.personal_history ||
          data?.other_details?.surgery_detail ||
          data?.other_details?.course_in_hospital ||
          data?.other_details?.condition_at_discharge ||
          data?.other_details?.medicine) && (
            <Section title="Clinical Summary & Patient History">
              {data?.other_details?.final_diagnosis && (
                <FullWidthField label="Final Diagnosis:" value={data.other_details.final_diagnosis} />
              )}
              {data?.other_details?.chief_complaints && (
                <FullWidthField label="Chief Complaints:" value={data.other_details.chief_complaints} />
              )}
              {data?.other_details?.past_history && (
                <FullWidthField label="Past History:" value={data.other_details.past_history} />
              )}
              {data?.other_details?.history && (
                <FullWidthField label="History:" value={data.other_details.history} />
              )}
              {data?.other_details?.personal_history && (
                <FullWidthField label="Personal History:" value={data.other_details.personal_history} />
              )}
              {data?.other_details?.surgery_detail && (
                <FullWidthField label="Surgery Details:" value={data.other_details.surgery_detail} />
              )}
              {data?.other_details?.course_in_hospital && (
                <FullWidthField label="Course In Hospital:" value={data.other_details.course_in_hospital} />
              )}
              {data?.other_details?.condition_at_discharge && (
                <FullWidthField label="Condition At Discharge:" value={data.other_details.condition_at_discharge} />
              )}
              {data?.other_details?.medicine && (
                <FullWidthField
                  label="Medicine Summary:"
                  value={data.other_details.medicine}
                  isLast={true}
                />
              )}
            </Section>
          )}

        {/* ── Clinical Examinations ─────────────────────────────────────── */}
        {(data?.clinical_examination?.consiouness_level ||
          data?.clinical_examination?.pulse ||
          data?.clinical_examination?.blood_pressure_systolic ||
          data?.clinical_examination?.blood_pressure_diastolic ||
          data?.clinical_examination?.temprature ||
          data?.clinical_examination?.respiratory_rate ||
          data?.clinical_examination?.chest ||
          data?.clinical_examination?.cvs ||
          data?.clinical_examination?.abdomen ||
          data?.clinical_examination?.cns ||
          data?.clinical_examination?.local_examination ||
          data?.clinical_examination?.any_other) && (
            <Section title="Clinical Examinations">
              <View style={styles.clinicalContainer}>
                {data?.clinical_examination?.consiouness_level && (
                  <ClinicalField label="Consciousness Level:" value={data.clinical_examination.consiouness_level} />
                )}
                {data?.clinical_examination?.pulse && (
                  <ClinicalField label="Pulse:" value={data.clinical_examination.pulse} />
                )}
                {(data?.clinical_examination?.blood_pressure_systolic ||
                  data?.clinical_examination?.blood_pressure_diastolic) && (
                    <ClinicalField
                      label="Blood Pressure:"
                      value={`${data.clinical_examination.blood_pressure_systolic || '-'}/${data.clinical_examination.blood_pressure_diastolic || '-'}`}
                    />
                  )}
                {data?.clinical_examination?.temprature && (
                  <ClinicalField label="Temperature:" value={data.clinical_examination.temprature} />
                )}
                {data?.clinical_examination?.respiratory_rate && (
                  <ClinicalField label="Respiratory Rate:" value={data.clinical_examination.respiratory_rate} />
                )}
                {data?.clinical_examination?.chest && (
                  <ClinicalField label="Chest:" value={data.clinical_examination.chest} />
                )}
                {data?.clinical_examination?.cvs && (
                  <ClinicalField label="CVS:" value={data.clinical_examination.cvs} />
                )}
                {data?.clinical_examination?.abdomen && (
                  <ClinicalField label="Abdomen:" value={data.clinical_examination.abdomen} />
                )}
                {data?.clinical_examination?.cns && (
                  <ClinicalField label="CNS:" value={data.clinical_examination.cns} />
                )}
                {data?.clinical_examination?.local_examination && (
                  <ClinicalField label="Local Examination:" value={data.clinical_examination.local_examination} />
                )}
                {data?.clinical_examination?.any_other && (
                  <ClinicalField label="Any Other:" value={data.clinical_examination.any_other} />
                )}
              </View>
            </Section>
          )}

        {/* ── Investigations & Diagnostics ─────────────────────────────── */}
        {(data?.lab?.lab_investigation || data?.radiology?.radiology) && (
          <Section title="Investigations & Diagnostics">
            {data?.lab?.lab_investigation && (
              <FullWidthField
                label="Laboratory Investigations:"
                value={data.lab.lab_investigation}
              />
            )}
            {data?.radiology?.radiology && (
              <FullWidthField
                label="Radiology Findings:"
                value={data.radiology.radiology}
                isLast={true}
              />
            )}
          </Section>
        )}

        {/* ── Prescribed Discharge Medications ─────────────────────────── */}
        {/*
          FIX SUMMARY for this section:
          1. Row style changed from array  →  single merged object spread
             BEFORE: style={[styles.tableRow, { backgroundColor: ... }]}
             AFTER:  style={{ ...styles.medicineRow, backgroundColor: ... }}
          2. `tableRow` renamed to `medicineRow` in StyleSheet to avoid collision
             with the HTML parser's internal table row rendering.
          3. paddingHorizontal on header/rows bumped from 2 → 4 for alignment.
        */}
        <Section title="Prescribed Discharge Medications">
          <View style={styles.tableContainer}>

            {/* Table Header */}
            <View style={styles.tableHeader}>
              <PDFText style={{ ...styles.colSNo, ...styles.tableHeaderTitle }}>#</PDFText>
              <PDFText style={{ ...styles.colMed, ...styles.tableHeaderTitle }}>Medicine Name</PDFText>
              <PDFText style={{ ...styles.colDose, ...styles.tableHeaderTitle }}>Dose</PDFText>
              <PDFText style={{ ...styles.colFreq, ...styles.tableHeaderTitle }}>Frequency</PDFText>
              <PDFText style={{ ...styles.colSchedule, ...styles.tableHeaderTitle }}>Schedule</PDFText>
              <PDFText style={{ ...styles.colDays, ...styles.tableHeaderTitle }}>Days</PDFText>
            </View>

            {/* Table Body */}
            {data?.medicine && data.medicine.length > 0 ? (
              data.medicine.map((item, index) => {
                // Build schedule string
                const scheduleTimes = [];
                if (item.morning === 'true' || item.morning === true) scheduleTimes.push('M');
                if (item.noon === 'true' || item.noon === true) scheduleTimes.push('N');
                if (item.evening === 'true' || item.evening === true) scheduleTimes.push('E');
                if (item.night === 'true' || item.night === true) scheduleTimes.push('Nt');
                const schedule = scheduleTimes.length > 0 ? scheduleTimes.join('-') : '-';

                const dose = item.dose_id?.dose || item.dose || '-';
                const frequency = item.frequency_id?.frequency || item.frequency || '-';
                const medicineName = decodeHtmlEntities(item.name || item.medicine_name || '-');

                return (
                  // ✅ FIX: single object spread — react-pdf does NOT support array styles
                  <View
                    key={index}
                    style={{
                      ...styles.medicineRow,
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : '#ffffff'
                    }}
                  >
                    <PDFText style={styles.colSNo}>{index + 1}</PDFText>
                    <PDFText style={styles.colMed}>{medicineName}</PDFText>
                    <PDFText style={styles.colDose}>{dose}</PDFText>
                    <PDFText style={styles.colFreq}>{frequency}</PDFText>
                    <PDFText style={styles.colSchedule}>{schedule}</PDFText>
                    <PDFText style={styles.colDays}>{item.days || '-'}</PDFText>
                  </View>
                );
              })
            ) : (
              <View style={styles.tableEmptyRow}>
                <PDFText style={styles.tableEmptyText}>No medicines prescribed.</PDFText>
              </View>
            )}
          </View>

          {/* Legend */}
          <View style={styles.tableLegend}>
            <PDFText style={styles.tableLegendBold}>Schedule Key:{'  '}</PDFText>
            <PDFText style={styles.tableLegendText}>
              M = Morning  |  N = Noon  |  E = Evening  |  Nt = Night
            </PDFText>
          </View>
        </Section>

        {/* ── Advice on Discharge ───────────────────────────────────────── */}
        {(data?.advice_on_discharge?.doctor_advice ||
          data?.advice_on_discharge?.emergency_contact_details ||
          data?.advice_on_discharge?.diet_plan) && (
            <Section title="Advice on Discharge">
              {data?.advice_on_discharge?.doctor_advice && (
                <FullWidthField
                  label="Doctor Advice:"
                  value={data.advice_on_discharge.doctor_advice}
                />
              )}
              {data?.advice_on_discharge?.emergency_contact_details && (
                <FullWidthField
                  label="Emergency Details:"
                  value={data.advice_on_discharge.emergency_contact_details}
                />
              )}
              {data?.advice_on_discharge?.diet_plan && (
                <FullWidthField
                  label="Diet Plan:"
                  value={data.advice_on_discharge.diet_plan}
                  isLast={true}
                />
              )}
            </Section>
          )}

        {/* ── General Examination (Rich Text) ──────────────────────────── */}
        {data?.general_examination && data.general_examination !== '-' && (
          <Section title="General Examination & Findings">
            <View style={styles.parsedContent}>
              {parseHtmlToPdfText(data.general_examination)}
            </View>
          </Section>
        )}

        {/* ── Footer & Signatures ───────────────────────────────────────── */}
        <View style={styles.signatureContainer}>
          <View style={styles.signatureBlock}>
            <Text content="Prepared By" style={styles.textBold} />
            <Text content={data?.createdBy || '-'} style={{ marginTop: 2 }} />
            <Text
              content={`Next Follow Up: ${followUpText}`}
              style={{ marginTop: 2, color: '#64748b', fontSize: 7.5 }}
            />
          </View>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text content="Consultant Doctor & Sign" style={styles.textBold} />
            <Text content={data?.consultant || '-'} style={{ marginTop: 2 }} />
          </View>
        </View>

      </Page>
    </Document>
  );

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        {PdfDoc}
      </PDFViewer>
    </div>
  );
};